<template>
  <section class="mb-20">
    <h2
      class="font-roboto text-2xl font-bold border-b border-royal-blue-20 pb-4 mb-6"
      id="recommendedReviewersTitle"
    >
      Recommended reviewers
    </h2>
    <p class="mb-6">
      You must provide a <strong>minimum of {{ minRecommendedReviewers }} and a maximum of {{ maxRecommendedReviewers }} recommended reviewers</strong>.
    </p>
    <draggable
      v-if="recommendedReviewers.length > 0"
      v-model="recommendedReviewers"
      :disabled="recommendedReviewers.length <= 1"
      item-key="id"
      tag="ul"
      id="pnlReviewerList"
      class="space-y-4 list-none p-0 mb-4">
      <template #item="{ element: reviewer }">
        <ReviewerListItem
          :key="reviewer.id"
          :showReason="true"
          :reviewer="reviewer"
          :isDraggable="recommendedReviewers.length > 1"
          @edit="openReviewerDialog" />
      </template>
    </draggable>

    <InlineWarningMessage
      v-if="canDisplayErrors && recommendedReviewers.length < 2"
      errorMessage="You must add a minimum of two recommended reviewers"
      errorTestId="minimum-two-recommended-reviewers"
      role="alert"
      class="mb-4"
    />
    <NotificationComponent
      v-if="errorMessage"
      :headingLevel="3"
      :headingText="errorMessage"
      variant="information"
      role="alert"
      class="mb-6"
    />
    <NotificationComponent
      v-if="hasReachedMaxRecommendedReviewers"
      :headingLevel="3"
      headingText="You have reached the maximum number of recommended reviewers"
      variant="information"
      role="status"
      class="mb-6"
    />
    <ButtonComponent
      label="Add recommended reviewer"
      variant="primary"
      data-testid="add-recommended-reviewer"
      id="addRecommendedReviewerButton"
      @customClick="addRecommendedReviewer"
      :disabled="hasReachedMaxRecommendedReviewers"
    />
  </section>
  <section class="mb-20">
    <h2 class="font-roboto text-2xl font-bold border-b border-royal-blue-20 pb-4 mb-6">
      Non-preferred reviewers
    </h2>
    <p class="mb-4">
      <strong>Optional</strong><br>
      You may add any number of non-preferred reviewers.
    </p>

    <draggable
      v-if="opposedReviewers.length > 0"
      v-model="opposedReviewers"
      :disabled="opposedReviewers.length <= 1"
      item-key="id"
      tag="ul"
      id="pnlOpposedReviewerList"
      class="space-y-4 list-none p-0 mb-4">
      <template #item="{ element: reviewer }">
        <ReviewerListItem
          :key="reviewer.id"
          :showReason="true"
          :reviewer="reviewer"
          :isDraggable="opposedReviewers.length > 1"
          @edit="openReviewerDialog" />
      </template>
    </draggable>

    <ButtonComponent
      label="Add non-preferred reviewer"
      variant="primary"
      id="addNonPreferredReviewerButton"
      data-testid="link-addNonPreferredReviewerButton"
      @customClick="addNonPreferredReviewer"
    />

    <EditReviewer
      v-if="isEditing"
      v-model:modelValue="isEditing"
      :value="reviewerToEdit"
      @close="closeReviewerDialog"
      @save="handleSaveReviewer"
      @delete="handleDeleteReviewer">
    </EditReviewer>
  </section>
</template>

<script setup lang="ts">
import { watch } from 'vue'
import EditReviewer from './EditReviewer.vue'
import ReviewerListItem from './ReviewerListItem.vue'
import ButtonComponent from '../ui-components/ButtonComponent.vue'
import NotificationComponent from '../ui-components/NotificationComponent.vue'
import { useField } from 'vee-validate'
import draggable from 'vuedraggable'
import InlineWarningMessage from '../common/InlineWarningMessage.vue'
import { useReviewerManagement } from '@/composables/useReviewerManagement'
import { ReviewerType } from '@/types'
import limits from '@/constants/limits'

defineProps({
  next: { type: Function, required: false },
  canDisplayErrors: { type: Boolean, required: false, default: false },
})

const {
  isEditing,
  reviewerToEdit,
  errorMessage,
  recommendedReviewers,
  opposedReviewers,
  hasReachedMaxRecommendedReviewers,
  addReviewer,
  openReviewerDialog,
  closeReviewerDialog,
  handleSaveReviewer,
  handleDeleteReviewer,
} = useReviewerManagement()

const addRecommendedReviewer = () => addReviewer(ReviewerType.Recommend)
const addNonPreferredReviewer = () => addReviewer(ReviewerType.Oppose)

const minRecommendedReviewers = limits.reviewers.counts.minRecommended
const maxRecommendedReviewers = limits.reviewers.counts.maxRecommended

const { value, validate } = useField('input-recommended-reviewers', 'maximum-number-of-recommended-reviewers-reached')

watch(recommendedReviewers, (newVal) => {
  value.value = newVal
  validate()
}, { immediate: true })
</script>
